import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  dayInZone,
  trackedRowNote,
  untrackedRowNote,
  GROUP_BY_VARIANT,
  type LogisticsAlertType,
  type RowNote,
} from '@d-orca/shared/alerts';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { fetchSiteLogistics, logisticsQueryRetry } from '@apis/logistics';
import { TruckIcon } from '@utils/icons';
import { lineNames, overdueDays } from '@d-orca/shared/client/logistics';
import type { Delivery } from '@interfaces/ILogistics';
import { buildLogisticsAlertGroups, groupCount, type ILogisticsAlertGroup } from '../../LogisticsPage/logisticsAlertGroups';
import { logisticsNoteLine } from './alertDetailLine';
import { DashboardCard } from './_shared/DashboardCard';
import styles from './deliveryCard.module.css';

/* Delivery — real half-width card fed by GET /api/sites/:siteId/logistics
   (same query key AND retry policy as the Logistics page, so the two share
   one cache entry and one failure behavior).

   The card IS the Deliveries & Stock alerts (2026-08-19 redesign): one list,
   one row per material the shared note chain flags — the same four groups the
   Logistics KPI tile and the System Alerts card count. Each row states its
   Logistics row's own sentence; the strip's pills are multi-select filters
   over the list, all pills off meaning "show everything".

   What this replaced: two top-N slices (four deliveries, three inventory
   rows) that on a site in trouble said "overdue" four times and "Ran out"
   three times, and quoted a stale count beside "Ran out". A late delivery
   appears as its own order row, "New delivery Nd overdue" — the alert
   vocabulary, not the board's parallel one. */

/** Rows shown before the rest compresses to "+N more". */
const MAX_ROWS = 6;

/** One listed alert: what it is about, and the Logistics sentence it quotes. */
interface IAlertRow {
  key: string;
  name: string;
  note: RowNote;
}

/** Pill class per alert group — the System Alerts card's palette: fill by
 *  severity, except the two "there is no figure here" groups (Ran out /
 *  Not tracked), which wear its dashed badges. */
const PILL_CLASS: Record<LogisticsAlertType, string> = {
  'stock-gap': styles.stripGap!,
  'stock-ran-out': styles.stripRanOut!,
  'delivery-late': styles.stripLate!,
  'stock-untracked': styles.stripUntracked!,
};

export function DeliveryCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  /** The pressed pills. Empty = no filter — every group's rows show. */
  const [selected, setSelected] = useState<ReadonlySet<LogisticsAlertType>>(new Set());
  /** Whether the list is unfolded past MAX_ROWS. */
  const [expanded, setExpanded] = useState(false);

  /* The project's timezone — the note chain measures "overdue" against the
     SITE's today. No default zone: the card holds its skeleton until the
     working site resolves rather than dating a delivery in a zone the site
     isn't in. */
  const tz = useSelector(selectSiteTz);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['logistics', siteId],
    queryFn: ({ signal }) => fetchSiteLogistics(siteId!, signal),
    enabled: Boolean(siteId),
    retry: logisticsQueryRetry,
  });

  const now = new Date();
  /* `now` is passed through so the strip and the list read ONE clock: the
     builder defaults to its own `new Date()`, and a render straddling the
     site's midnight would otherwise group against one calendar day and list
     against another. */
  const groups =
    data && tz ? buildLogisticsAlertGroups(data.inventory, data.untracked, data.deliveries, tz, now) : [];

  /* Every red or amber note the Logistics rows are currently drawing, with the
     material it belongs to — the same chain the strip counts are built from,
     kept per-row so the list can quote each sentence. Inventory arrives
     risk-sorted, so within a group the worst rows lead; untracked follow. */
  const today = tz ? dayInZone(now, tz) : '';
  const noted: IAlertRow[] =
    data && tz
      ? [
          ...data.inventory.map((level) => ({
            key: level.materialName,
            name: level.materialName,
            note: trackedRowNote(level, today, tz),
          })),
          ...data.untracked.map((material) => ({
            key: material.materialName,
            name: material.materialName,
            note: untrackedRowNote(material, today),
          })),
        ].filter((row): row is IAlertRow => row.note !== null)
      : [];

  /* Delivery late counts ORDERS (`group.refs`), so it lists them too — one row
     per overdue order, in the board's own words — rather than the material
     rows whose note happens to say "late", which would neither match the pill
     nor name the trucks it counts. */
  const orderRow = (order: Delivery): IAlertRow => ({
    key: order.ref,
    name: `#${order.ref} · ${lineNames(order).join(t('common.listSeparator'))}`,
    note: { variant: 'late-in-flight', value: overdueDays(order, now, tz!), date: order.dueDate!, ref: order.ref },
  });

  /* Group order (critical band first) comes from `groups`, so the list and
     the strip can never disagree about what leads.

     The filter is the pressed pills ∩ the groups that still exist: `selected`
     can hold a group that resolved upstream since it was pressed (the query
     shares its cache with the Logistics page, whose mutations refetch it), and
     honouring that stale entry would strand the card on an empty list with no
     visibly-pressed pill to clear. When nothing pressed survives, the card
     shows everything — the same reading as no filter at all. */
  const liveSelected = groups.filter((g) => selected.has(g.type)).map((g) => g.type);
  const shownGroups = groups.filter((g) => liveSelected.length === 0 || selected.has(g.type));
  const rows = shownGroups.flatMap((group) =>
    group.refs
      ? data!.deliveries.filter((order) => group.refs!.includes(order.ref)).map(orderRow)
      : noted.filter((row) => GROUP_BY_VARIANT[row.note.variant] === group.type),
  );

  const toggle = (type: LogisticsAlertType) => {
    // Rebuilt from the LIVE entries, so a resolved group pressed long ago
    // doesn't spring back as a filter if its alert ever recurs.
    const next = new Set(liveSelected);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    setSelected(next);
  };

  /** A group's pill — its count and the System Alerts card's own type label.
   *  A toggle, not a link: pills compose, and all-off means everything. */
  const stripPill = (group: ILogisticsAlertGroup) => (
    <button
      key={group.type}
      type="button"
      className={`${styles.stripPill} ${PILL_CLASS[group.type]}`}
      aria-pressed={selected.has(group.type)}
      data-dimmed={liveSelected.length > 0 && !selected.has(group.type) ? true : undefined}
      onClick={() => toggle(group.type)}
    >
      {groupCount(group)} {t(`dashboard.activityAlerts.types.${group.type}`)}
    </button>
  );

  return (
    <DashboardCard
      title={t('dashboard.delivery.title')}
      subtitle={t('dashboard.delivery.subtitle')}
      titleIcon={<span className={styles.titleIconTruck}><TruckIcon /></span>}
      headerRight={
        <Link to={`/logistics/${siteId}`} className={styles.viewAll}>
          {t('dashboard.delivery.viewAll')}
        </Link>
      }
      bodyClassName={styles.body}
    >
      {isLoading || !tz ? (
        <div className={styles.skeletonList} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className={styles.skeletonRow} />)}
        </div>
      ) : isError ? (
        <span className={styles.stateLine}>{t('dashboard.delivery.error')}</span>
      ) : data ? (
        groups.length === 0 ? (
          <span className={styles.stateLine}>{t('dashboard.delivery.allClear')}</span>
        ) : (
          <>
            <div className={styles.strip}>{groups.map(stripPill)}</div>
            <ul className={styles.alertList}>
              {(expanded ? rows : rows.slice(0, MAX_ROWS)).map(({ key, name, note }) => (
                <li key={`${key}:${note.variant}`} className={styles.alertRow}>
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{name}</span>
                    {/* The Logistics panel's own sentence for this row —
                        shared keys, so the two surfaces cannot drift. */}
                    <span className={styles.rowMeta}>{logisticsNoteLine(note.variant, note, t)}</span>
                  </div>
                  <span className={`${styles.stripPill} ${PILL_CLASS[GROUP_BY_VARIANT[note.variant]]}`}>
                    {t(`dashboard.activityAlerts.types.${GROUP_BY_VARIANT[note.variant]}`)}
                  </span>
                </li>
              ))}
            </ul>
            {rows.length > MAX_ROWS ? (
              /* The fold — same control as the Logistics KPI tile's, sharing
                 its "Less" copy so the two folds read as one pattern. */
              <button
                type="button"
                className={styles.moreButton}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded
                  ? t('logistics.kpi.fewerReasons')
                  : t('dashboard.activityAlerts.group.moreMembers', { count: rows.length - MAX_ROWS })}
              </button>
            ) : null}
          </>
        )
      ) : null}
    </DashboardCard>
  );
}
